import { useNavigate } from 'react-router-dom';
import { useListFilters } from '@/client/hooks/useListFilters';
import { LayoutDashboard, Loader2 } from 'lucide-react';
import { SALES_VERTICALS, VERTICAL_LABEL, type SalesShareRow, type SalesVertical } from '@/client/types/sales';
import { SalesPageShell } from './components/SalesPageShell';
import { ShareSiteName } from './components/ShareSiteName';
import { LiveSourcesPanel } from './components/LiveSourcesPanel';
import { LeadsPanel } from './components/LeadsPanel';
import {
  CopyLinkButton,
  EmptyState,
  MarketplaceIndexState,
  Panel,
  compactGhs,
  pct,
  selectCls,
} from './components/sales-ui';
import { getApiErrorCode } from '@/client/utils/errors';
import { useSalesShareQuery } from '@/client/api/sales/sales';
import {
  SHARE_PERIODS,
  SHARE_PERIOD_LABEL,
  SHARE_SITE_COLOR,
  SHARE_SITE_LABEL,
  type SharePeriod,
  type ShareSiteId,
} from './prospect-share';

function sharePct(n: number | null | undefined): string {
  if (n == null) return '—';
  const p = n * 100;
  if (Math.abs(p) < 1) return `${p.toFixed(2)}%`;
  return `${Math.round(p)}%`;
}

function asShareSite(id: string): ShareSiteId | null {
  return id in SHARE_SITE_LABEL ? (id as ShareSiteId) : null;
}

function DualNum({
  live,
  posted,
  postedKnown,
  money,
}: {
  live: number;
  posted: number;
  postedKnown: boolean;
  money?: boolean;
}) {
  const fmt = money ? compactGhs : (n: number) => n.toLocaleString();
  return (
    <div className="text-right tabular-nums">
      <div className="text-gray-900">{fmt(live)}</div>
      <div className="text-[11px] text-gray-500">{postedKnown ? `${fmt(posted)} posted` : 'posted unknown'}</div>
    </div>
  );
}

function SiteLabel({ row }: { row: SalesShareRow }) {
  const id = asShareSite(row.competitor);
  if (id) return <ShareSiteName id={id} />;
  const mark = (row.label || row.competitor || '?').trim().slice(0, 1).toUpperCase() || '?';
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-[10px] font-semibold text-white"
        style={{ backgroundColor: '#64748b' }}
      >
        {mark}
      </span>
      <span>{row.label || row.competitor}</span>
    </span>
  );
}

export default function SalesProspectsOverviewPage() {
  const navigate = useNavigate();
  // Filters live in the URL so a desk/period view is shareable and bookmarkable.
  const { filters, handleFilterChange } = useListFilters({
    initialFilters: { period: '30d' as SharePeriod, vertical: 'all' as SalesVertical | 'all' },
  });
  const period = filters.period;
  const vertical = filters.vertical;
  const shareQ = useSalesShareQuery({ period, vertical });
  const share = shareQ.data;
  const forbidden = getApiErrorCode(shareQ.error) === 'upstream_forbidden';
  const rows = share?.rows ?? [];
  const listingMax = Math.max(1, ...rows.map((r) => (r.postedKnown ? r.postedListings : 0)));
  const postedHint = period === 'all' ? 'all-time posted' : `posted in ${SHARE_PERIOD_LABEL[period].toLowerCase()}`;
  const ready = Boolean(share) && !forbidden && !shareQ.isError;
  const ffRow = rows.find((r) => r.isFeelyFeely);
  const otherLive = rows.filter((r) => !r.isFeelyFeely).reduce((n, r) => n + r.liveSellers, 0);

  return (
    <SalesPageShell
      title="Overview"
      description="FeelyFeely against the other sites in this prospect index — sellers, listings, paid vs featured."
      crumbs={[{ label: 'Prospects', href: '/admin/sales/prospects/overview' }, { label: 'Overview' }]}
      icon={LayoutDashboard}
      stats={[
        {
          key: 'ff',
          label: 'On FeelyFeely',
          value: ready ? (ffRow?.liveSellers ?? '—') : '—',
        },
        {
          key: 'comp',
          label: 'Other live sellers',
          value: ready ? otherLive.toLocaleString() : '—',
        },
        {
          key: 'list',
          label: 'Listing share',
          value: ready ? sharePct(share?.feelyfeely.postedListingShare) : '—',
          hint: postedHint,
        },
        {
          key: 'inv',
          label: 'Live inventory',
          value: ready ? sharePct(share?.feelyfeely.liveInventoryShare) : '—',
          hint: 'of the indexed set',
        },
        {
          key: 'asof',
          label: 'Index as of',
          value: ready && share?.asOf ? share.asOf.slice(0, 10) : '—',
        },
      ]}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={selectCls}
            value={period}
            onChange={(e) => handleFilterChange('period', e.target.value)}
            aria-label="Period"
          >
            {SHARE_PERIODS.map((p) => (
              <option key={p} value={p}>
                {SHARE_PERIOD_LABEL[p]}
              </option>
            ))}
          </select>
          <select
            className={selectCls}
            value={vertical}
            onChange={(e) => handleFilterChange('vertical', e.target.value)}
            aria-label="Desk"
          >
            <option value="all">All desks</option>
            {SALES_VERTICALS.map((v) => (
              <option key={v} value={v}>
                {VERTICAL_LABEL[v]}
              </option>
            ))}
          </select>
          <CopyLinkButton label="Copy link" />
        </div>
      }
    >
      <LiveSourcesPanel country="ghana" />
      {shareQ.isError ? (
        <MarketplaceIndexState error={shareQ.error} unavailable="Share is unavailable." />
      ) : shareQ.isLoading ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-4 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading share…
        </div>
      ) : share?.note && rows.length === 0 ? (
        <EmptyState label={share.note} />
      ) : rows.length === 0 ? (
        <EmptyState label="No indexed sellers for this desk." />
      ) : (
        <>
          <section className="mb-4">
            <Panel title="Sellers and listings">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <caption className="sr-only">Indexed-set share by site</caption>
                  <thead className="border-b border-gray-200 text-left text-xs font-medium text-gray-800">
                    <tr>
                      <th scope="col" className="py-2 pr-4">
                        Site
                      </th>
                      <th scope="col" className="py-2 pr-4 text-right">
                        Live sellers
                      </th>
                      <th scope="col" className="py-2 pr-4 text-right">
                        Listings
                        <span className="block font-normal text-gray-500">live / posted</span>
                      </th>
                      <th scope="col" className="hidden py-2 pr-4 text-right sm:table-cell">
                        Inventory
                        <span className="block font-normal text-gray-500">live / posted</span>
                      </th>
                      <th scope="col" className="hidden py-2 pr-4 text-right lg:table-cell">
                        Still live
                      </th>
                      <th scope="col" className="hidden py-2 pr-4 text-right sm:table-cell">
                        Paid
                      </th>
                      <th scope="col" className="hidden py-2 pr-4 text-right sm:table-cell">
                        Featured
                      </th>
                      <th scope="col" className="py-2 pl-2">
                        Posted mix
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const id = asShareSite(row.competitor);
                      return (
                        <tr key={row.competitor} className="border-b border-gray-100 last:border-0">
                          <td className="py-2.5 pr-4 font-medium text-gray-900">
                            <SiteLabel row={row} />
                          </td>
                          <td className="py-2.5 pr-4 text-right tabular-nums">{row.liveSellers.toLocaleString()}</td>
                          <td className="py-2.5 pr-4">
                            <DualNum live={row.liveListings} posted={row.postedListings} postedKnown={row.postedKnown} />
                          </td>
                          <td className="hidden py-2.5 pr-4 sm:table-cell">
                            <DualNum
                              live={row.liveInventoryGhs}
                              posted={row.postedInventoryGhs}
                              postedKnown={row.postedKnown}
                              money
                            />
                          </td>
                          <td className="hidden py-2.5 pr-4 text-right tabular-nums lg:table-cell">
                            {row.stillLive == null ? '—' : pct(row.stillLive)}
                          </td>
                          <td className="hidden py-2.5 pr-4 text-right tabular-nums sm:table-cell">
                            {row.paidSellers.toLocaleString()}
                          </td>
                          <td className="hidden py-2.5 pr-4 text-right tabular-nums sm:table-cell">
                            {row.premiumSellers.toLocaleString()}
                          </td>
                          <td className="py-2.5 pl-2">
                            {row.postedKnown ? (
                              <div className="flex h-2 min-w-[6rem] overflow-hidden rounded-full bg-gray-100">
                                <div
                                  className="h-full"
                                  style={{
                                    width: `${row.postedListings ? Math.max(4, (row.postedListings / listingMax) * 100) : 0}%`,
                                    backgroundColor: id ? SHARE_SITE_COLOR[id] : '#94a3b8',
                                  }}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">Unknown</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>
          </section>

          <div className="mb-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Paid vs featured sellers">
              <ul className="space-y-3">
                {rows.map((row) => {
                  const n = row.liveSellers;
                  return (
                    <li key={row.competitor}>
                      <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                        <SiteLabel row={row} />
                        <span className="text-xs tabular-nums text-gray-500">
                          {row.paidSellers.toLocaleString()} paid · {row.premiumSellers.toLocaleString()} featured
                        </span>
                      </div>
                      <div className="flex h-2 overflow-hidden rounded-full bg-gray-100">
                        {n > 0 ? (
                          <div
                            className="bg-slate-900"
                            style={{ width: `${Math.min(100, (row.paidSellers / n) * 100)}%` }}
                          />
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Panel>

            <LeadsPanel
              country="ghana"
              isPlaces={vertical === 'places'}
              onOpenSearch={() => navigate('/admin/sales/prospects/search')}
            />
          </div>
        </>
      )}
    </SalesPageShell>
  );
}
